/**
 * FleetMap — operational fleet monitoring map.
 *
 * Shows all drones on a Leaflet map with status-coloured markers.
 * Clicking a marker navigates to the drone detail page.
 *
 * Drone positions: since drones don't yet stream live GPS, the map assigns
 * each drone a deterministic spread around the selected centre of Israel so
 * the map is visually meaningful and ready to receive real coordinates later.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './FleetMap.css'

const STREET_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const SAT_URL    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

export type DroneMarker = {
  id: string
  drone_id: string
  tail_number?: string
  status: string
  drone_model?: string
  lat?: number
  lng?: number
}

interface FleetMapProps {
  drones: DroneMarker[]
  height?: number
}

// Status → colour mapping
const STATUS_COLOR: Record<string, string> = {
  online:      '#4ade80',
  active:      '#4ade80',
  flying:      '#60a5fa',
  offline:     '#6b7280',
  maintenance: '#fbbf24',
  error:       '#f87171',
  warning:     '#fb923c',
}

// Generate a deterministic offset from a string so each drone gets a stable position
function deterministicOffset(seed: string, scale = 1.8): [number, number] {
  let h = 5381
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h) ^ seed.charCodeAt(i)
    h = h & 0x7fffffff
  }
  const lat = ((h % 1000) / 1000 - 0.5) * scale
  const lng = (((h >> 10) % 1000) / 1000 - 0.5) * scale * 1.5
  return [lat, lng]
}

const CENTER: [number, number] = [31.5, 34.9]
const DEFAULT_ZOOM = 8

export default function FleetMap({ drones, height = 360 }: FleetMapProps) {
  const mapRef = useRef<L.Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const markersRef = useRef<L.Marker[]>([])
  const navigate = useNavigate()
  const [satellite, setSatellite] = useState(false)

  // Toggle tile layer when satellite state changes
  useEffect(() => {
    tileLayerRef.current?.setUrl(satellite ? SAT_URL : STREET_URL)
  }, [satellite])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      center: CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
    })

    tileLayerRef.current = L.tileLayer(STREET_URL, {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map)

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      tileLayerRef.current = null
      markersRef.current = []
    }
  }, [])

  // Re-draw markers when drones change
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Remove old markers
    markersRef.current.forEach(m => m.remove())
    markersRef.current = []

    drones.forEach(drone => {
      // Use provided coords or deterministic spread
      let lat = drone.lat
      let lng = drone.lng
      if (lat == null || lng == null) {
        const [dLat, dLng] = deterministicOffset(drone.id)
        lat = CENTER[0] + dLat
        lng = CENTER[1] + dLng
      }

      const color = STATUS_COLOR[drone.status] ?? '#9ca3af'
      const html = `
        <div class="fleet-marker" style="--mc:${color}">
          <div class="fleet-marker-dot"></div>
          <div class="fleet-marker-pulse"></div>
        </div>`

      const icon = L.divIcon({
        className: '',
        html,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      })

      const marker = L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(`
          <div class="fleet-popup">
            <strong>${drone.drone_id}</strong>
            ${drone.tail_number ? `<div class="fp-tail">זנב: ${drone.tail_number}</div>` : ''}
            ${drone.drone_model ? `<div class="fp-model">${drone.drone_model}</div>` : ''}
            <div class="fp-status" style="color:${color}">${drone.status}</div>
            <a class="fp-link" href="/drones/${drone.id}">פתח פרטי רחפן →</a>
          </div>
        `)

      marker.on('click', () => {
        navigate(`/drones/${drone.id}`)
      })

      markersRef.current.push(marker)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drones])

  return (
    <div className="fleet-map-wrap" style={{ height }}>
      <div ref={mapContainerRef} className="fleet-map-canvas" style={{ height }} />
      <button
        className="fleet-sat-toggle"
        onClick={() => setSatellite(v => !v)}
        title={satellite ? 'מפת רחובות' : 'תצלום לוויין'}
      >
        {satellite ? '🗺' : '🛰'}
      </button>
      {drones.length === 0 && (
        <div className="fleet-map-empty">אין רחפנים לתצוגה</div>
      )}
    </div>
  )
}
